import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../contexts/AdminContext";

const APPEAR_DELAY_MS = 3500;
const STORAGE_KEY = "alert-banner-dismissed-v1";

function getDaysLeft(dateStr: string): number {
  const drawDate = new Date(dateStr);
  if (isNaN(drawDate.getTime())) return 0;
  
  const diff = drawDate.getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400000);
}

export default function AlertBanner() {
  const { settings } = useAdmin();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [daysLeft, setDaysLeft] = useState(() => getDaysLeft(settings.nextDrawDate));

  useEffect(() => {
    // Don't show if already dismissed
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const timer = setTimeout(() => {
      setVisible(true);
      // small delay so CSS transition fires properly
      setTimeout(() => setMounted(true), 20);
    }, APPEAR_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // Update daysLeft when settings.nextDrawDate changes or every minute
  useEffect(() => {
    setDaysLeft(getDaysLeft(settings.nextDrawDate));
    const iv = setInterval(() => setDaysLeft(getDaysLeft(settings.nextDrawDate)), 60000);
    return () => clearInterval(iv);
  }, [settings.nextDrawDate]);

  const handleDismiss = () => {
    setMounted(false);
    setTimeout(() => setVisible(false), 400);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  if (!visible) return null;

  const urgency = daysLeft <= 7;
  
  // Use custom alert text if it's set in admin, otherwise use the dynamic countdown label
  const label = settings.alertText || (
    daysLeft === 0
      ? "Heute — letzter Tag zur Teilnahme!"
      : daysLeft === 1
      ? "Nur noch 1 Tag bis zur Ziehung!"
      : `Noch ${daysLeft} Tage bis zur Ziehung`
  );

  return (
    <div
      role="alert"
      style={{
        maxHeight: mounted ? "60px" : "0px",
        opacity: mounted ? 1 : 0,
        transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease",
        overflow: "hidden",
      }}
      className={`relative w-full z-[100] ${urgency ? "bg-red-600 font-bold" : "bg-primary"}`}
    >
      <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between gap-3">
        {/* Left pulse dot + Label */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className={`shrink-0 w-1.5 h-1.5 rounded-full animate-pulse ${
              urgency ? "bg-red-200" : "bg-primary-dark/50"
            }`}
          />
          <p
            className={`text-[10px] sm:text-sm font-bold truncate ${
              urgency ? "text-white" : "text-white uppercase tracking-tighter"
            }`}
          >
            {label}
          </p>
        </div>

        {/* CTA + close */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/forfaits"
            className={`hidden md:block text-[10px] font-black px-4 py-1.5 rounded-full whitespace-nowrap transition-all duration-300 ${
              urgency
                ? "bg-white text-red-600 hover:bg-red-50"
                : "bg-primary-dark text-white hover:bg-primary-dark shadow-md"
            }`}
          >
            JETZT TEILNEHMEN →
          </Link>
          <button
            onClick={handleDismiss}
            aria-label="Fermer le bandeau"
            className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              urgency
                ? "text-white/60 hover:text-white"
                : "text-white/60 hover:text-white"
            }`}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
